import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { Button, Badge, Modal, FormInput, FormTextarea, FormSelect, PageHeader, ConfirmDialog } from '../components/ui';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { formatDateTime } from '../utils/dateUtils';

interface Notification {
  id: number;
  title: string;
  content: string;
  date: string;
  status: string;
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    status: 'active',
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/notifications');
      if (response.data.status) {
        setNotifications(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setIsDeleting(true);
    try {
      const response = await apiClient.delete(`/admin/notifications/${deleteId}`);
      if (response.data.status) {
        setNotifications(notifications.filter(n => n.id !== deleteId));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const handleToggleStatus = async (notification: Notification) => {
    const newStatus = notification.status === 'active' ? 'inactive' : 'active';
    try {
      const response = await apiClient.put(`/admin/notifications/${notification.id}`, { status: newStatus });
      if (response.data.status) {
        setNotifications(notifications.map(n =>
          n.id === notification.id ? { ...n, status: newStatus } : n
        ));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editingId) {
        const response = await apiClient.put(`/admin/notifications/${editingId}`, formData);
        if (response.data.status) {
          setNotifications(notifications.map(n =>
            n.id === editingId ? response.data.data : n
          ));
        }
      } else {
        const response = await apiClient.post('/admin/notifications', formData);
        if (response.data.status) {
          setNotifications([response.data.data, ...notifications]);
        }
      }
      closeModal();
    } catch (error) {
      console.error('Failed to save notification:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const openCreateModal = () => {
    setFormData({ title: '', content: '', status: 'active' });
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (n: Notification) => {
    setFormData({ title: n.title, content: n.content, status: n.status });
    setEditingId(n.id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const filteredNotifications = notifications.filter(n =>
    n.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns: Column[] = [
    {
      key: 'id',
      label: 'ID',
      width: 'w-12',
      render: (val) => <span className="text-slate-500 text-xs font-mono">#{val}</span>,
    },
    {
      key: 'title',
      label: 'Title',
      render: (val) => <span className="text-white font-semibold">{val}</span>,
    },
    {
      key: 'content',
      label: 'Content',
      render: (val) => (
        <span className="text-slate-400 max-w-xs block truncate" title={val}>{val}</span>
      ),
    },
    {
      key: 'date',
      label: 'Date',
      render: (val) => (
        <span className="text-slate-400 text-xs">{formatDateTime(val)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      textAlign: 'center',
      render: (val) => (
        <Badge variant={val === 'active' ? 'active' : 'inactive'} dot>
          {val === 'active' ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      textAlign: 'center',
      render: (_val, item) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => handleToggleStatus(item)}
          >
            {item.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            variant="indigo"
            size="xs"
            onClick={() => openEditModal(item)}
          >
            Edit
          </Button>
          <Button
            variant="danger"
            size="xs"
            onClick={() => setDeleteId(item.id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">
      <PageHeader
        title="Notifications"
        subtitle="Manage and broadcast global notifications to all users in real-time."
      />

      <StakeTable
        title=""
        columns={columns}
        data={filteredNotifications}
        isLoading={isLoading}
        searchPlaceholder="Search by title or content..."
        onSearch={setSearchTerm}
        emptyMessage="No notifications found. Create one to get started."
        hasSidebarMargin={false}
        actions={
          <button
            onClick={openCreateModal}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm whitespace-nowrap text-sm h-9"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Notification
          </button>
        }
      />

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingId ? 'Edit Notification' : 'Create Notification'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              loading={isSaving}
              onClick={(e) => handleSubmit(e as any)}
            >
              {editingId ? 'Save Changes' : 'Create & Broadcast'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Title"
            required
            placeholder="Enter notification title..."
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
          />
          <FormTextarea
            label="Content"
            required
            rows={4}
            placeholder="Enter notification content..."
            value={formData.content}
            onChange={e => setFormData({ ...formData, content: e.target.value })}
          />
          <FormSelect
            label="Status"
            value={formData.status}
            onChange={e => setFormData({ ...formData, status: e.target.value })}
            options={[
              { value: 'active', label: 'Active — Visible to all users' },
              { value: 'inactive', label: 'Inactive — Hidden from users' },
            ]}
          />
          {!editingId && (
            <div className="flex items-start gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-md px-3 py-2">
              <svg className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-indigo-300">
                Creating an <strong>Active</strong> notification will instantly broadcast it to all online users via WebSocket.
              </p>
            </div>
          )}
        </form>
      </Modal>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Notification"
        message="Are you sure you want to permanently delete this notification? This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={isDeleting}
      />
    </div>
  );
};

export default Notifications;
